import type { ReactNode } from "react";
import { cn } from "@/lib/utils";
import { DataTable, type DataTableColumn } from "@/components/ui/data-table";
import { Icon } from "@/components/ui/icon";

type ChangeKind = "added" | "removed" | "changed" | "context" | "truncated";

interface ParsedChange {
  kind: ChangeKind;
  text: string;
}

function parseExcerpt(excerpt: string): ParsedChange[] {
  return excerpt.split("\n").filter(Boolean).map((line) => {
    if (line === "…") return { kind: "truncated", text: "More changes available in the full alert." };
    if (line.startsWith("+ ")) return { kind: "added", text: line.slice(2) };
    if (line.startsWith("- ")) return { kind: "removed", text: line.slice(2) };
    if (line.startsWith("~ ")) return { kind: "changed", text: line.slice(2) };
    return { kind: "context", text: line };
  });
}

const CHANGE_STYLES: Record<ChangeKind, { marker: string; row: string; markerColor: string }> = {
  added: { marker: "+", row: "border-emerald-500/20 bg-emerald-500/5", markerColor: "text-emerald-400" },
  removed: { marker: "−", row: "border-red-500/20 bg-red-500/5", markerColor: "text-red-400" },
  changed: { marker: "~", row: "border-amber-500/20 bg-amber-500/5", markerColor: "text-amber-400" },
  context: { marker: "·", row: "border-border/60 bg-muted/10", markerColor: "text-muted-foreground" },
  truncated: { marker: "…", row: "border-border/60 bg-muted/10", markerColor: "text-muted-foreground" },
};

export function ChangeExcerpt({ excerpt, action }: { excerpt: string; action?: ReactNode }) {
  const changes = parseExcerpt(excerpt);
  const additions = changes.filter((change) => change.kind === "added").length;
  const removals = changes.filter((change) => change.kind === "removed").length;
  const edits = changes.filter((change) => change.kind === "changed").length;
  const rows = changes.map((change, index) => ({ ...change, id: `${change.kind}-${index}` }));
  const columns: DataTableColumn<(typeof rows)[number]>[] = [
    {
      id: "type",
      header: "Type",
      width: "8rem",
      cell: (row) => {
        const style = CHANGE_STYLES[row.kind];
        const icon = row.kind === "added" ? "arrow-up-01" : row.kind === "removed" ? "arrow-down-01" : row.kind === "changed" ? "edit-02" : "more-horizontal";
        return (
          <span className={cn("inline-flex items-center gap-2", style.markerColor)} title={row.kind}>
            <Icon name={icon} size={13} aria-hidden="true" />
            <span className="sr-only">{row.kind}</span>
          </span>
        );
      },
    },
    {
      id: "change",
      header: "Change",
      cell: (row) => <span className="break-words font-mono text-[11px] leading-relaxed text-foreground/70">{row.text}</span>,
    },
  ];

  return (
    <div>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 rounded-lg border border-border bg-muted/20 px-4 py-3">
        <div className="flex flex-wrap items-center gap-3">
          <span className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">Change excerpt</span>
          <div className="flex items-center gap-3 font-mono text-[11px]">
            {additions > 0 && <span className="text-emerald-400">{additions} added</span>}
            {removals > 0 && <span className="text-red-400">{removals} removed</span>}
            {edits > 0 && <span className="text-amber-400">{edits} edited</span>}
          </div>
        </div>
        {action}
      </div>
      <DataTable
        columns={columns}
        rows={rows}
        rowKey={(row) => row.id}
        minWidth={560}
        verticalDividers
        rowClassName={(row) => CHANGE_STYLES[row.kind].row}
      />
    </div>
  );
}